import { Typography } from '@mui/material';
import { compact } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { SpinningIcon } from '@/components/animation/SpinningIcon/SpinningIcon';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { RefreshCw05Icon } from '@/components/icons/RefreshCw05Icon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { EmptyCell } from '@/components/tables/DataTable/renderers/cell/EmptyCell';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { getDocumentSummaryBraintrustLogsUrl } from '@/modules/braintrust/braintrustLogs';
import { useBraintrustLinks } from '@/modules/braintrust/useBraintrustLinks';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useHouseholdWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts';
import { isMostRecentDocumentWorkflowTrackerInProgress } from '@/modules/workflowTrackers/utils/documentWorkflowTrackers';
import { COLORS } from '@/styles/tokens/colors';
import { DocumentType, WorkflowTrackerKind } from '@/types/schema';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { getDocumentAttributionText } from '@/utils/formatting/documentAttribution';
import { getNodes } from '@/utils/graphqlUtils';

import {
  getEntitiesFromRelationships,
  getTestamentaryEntitiesFromRelationships,
} from '../../documentRelationships.utils';
import { AssociatedEntitiesRightContent } from '../components/AssociatedEntitiesRightContent';
import { CopyDocumentKnowledgeGraphV2ActionItem } from '../components/CopyDocumentKnowledgeGraphV2ActionItem';
import { CopyDocumentOcrMarkdownActionItem } from '../components/CopyDocumentOcrMarkdownActionItem';
import { CopyDocumentTextActionItem } from '../components/CopyDocumentTextActionItem';
import { CopyInferredDataActionItem } from '../components/CopyInferredDataActionItem';
import { DeleteDocumentActionItem } from '../components/DeleteDocumentActionItem';
import { DownloadDocumentActionItem } from '../components/DownloadDocumentActionItem';
import { ViewDocumentKnowledgeGraphV2ActionItem } from '../components/ViewDocumentKnowledgeGraphV2ActionItem';
import { DocumentsTableRowData } from '../DocumentsTable.types';
import { DocumentsTable_DocumentFragment } from '../graphql/DocumentsTable.generated';

interface MapDocumentsToRowsAndColumnsOpts {
  refetch: () => void;
  entityId?: string;
  householdId?: string;
}

export function useMapDocumentsToRowsAndColumns(
  documents: DocumentsTable_DocumentFragment[],
  opts: MapDocumentsToRowsAndColumnsOpts
) {
  const currentUser = useCurrentUser();
  const { showDiagnosticLinks } = useBraintrustLinks();
  const canDeleteDocument = useCanEditHouseholdData();
  const { trackers } = useHouseholdWorkflowTrackersContext();

  const aiEnabled = useAICapabilitiesEnabled();

  // Documents with a delete in flight. Used to disable each row's delete
  // action so a slow delete can't be re-triggered from a re-opened menu (the
  // per-row menu unmounts on close, so a local flag wouldn't survive). Held in
  // the table, which stays mounted; cleared if the delete fails.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  const markDeleting = useCallback((id: string) => {
    setPendingDeleteIds((prev) => new Set(prev).add(id));
  }, []);
  const unmarkDeleting = useCallback((id: string) => {
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const documentIdsWithProcessingSummaries = useMemo(() => {
    const documentIds = new Set<string>();

    documents.forEach((document) => {
      if (
        isMostRecentDocumentWorkflowTrackerInProgress(trackers, {
          documentId: document.id,
          kind: WorkflowTrackerKind.DocumentSummary,
        })
      ) {
        documentIds.add(document.id);
      }
    });

    return documentIds;
  }, [documents, trackers]);

  const actions: Column<DocumentsTableRowData> = useMemo(() => {
    return {
      field: 'actions',
      headerName: '',
      width: 64,
      minWidth: 64,
      maxWidth: 64,
      sortable: false,
      align: 'center',
      renderCell: ActionButtonRenderer({
        actions: compact([
          {
            MenuItem: (row) => (
              <DownloadDocumentActionItem
                row={row}
                showDivider={!!currentUser?.isInternalUser || canDeleteDocument}
              />
            ),
          },
          showDiagnosticLinks &&
            opts.householdId && {
              MenuItem: (row) => {
                const mostRecentSummaryId = row.mostRecentSummaryId;

                if (!mostRecentSummaryId) {
                  return null;
                }

                return (
                  <MenuItem
                    icon={<LinkExternal01Icon size={16} />}
                    label="View Braintrust logs"
                    onClick={() =>
                      window.open(
                        getDocumentSummaryBraintrustLogsUrl({
                          householdId: opts.householdId!,
                          documentId: row.id,
                          documentSummaryId: mostRecentSummaryId,
                        }),
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  />
                );
              },
            },
          currentUser?.isInternalUser && {
            MenuItem: (row) => (
              <CopyDocumentTextActionItem
                disabled={row.type === DocumentType.GeneratedPresentation}
                row={row}
              />
            ),
          },
          currentUser?.isInternalUser && {
            MenuItem: (row) => {
              return (
                <CopyDocumentOcrMarkdownActionItem
                  disabled={row.type === DocumentType.GeneratedPresentation}
                  row={row}
                />
              );
            },
          },
          currentUser?.isInternalUser &&
            opts.householdId && {
              MenuItem: (row) => (
                <CopyDocumentKnowledgeGraphV2ActionItem
                  row={row}
                  householdId={opts.householdId!}
                  disabled={row.type === DocumentType.GeneratedPresentation}
                />
              ),
            },
          currentUser?.isInternalUser &&
            opts.householdId && {
              MenuItem: (row) => (
                <ViewDocumentKnowledgeGraphV2ActionItem
                  row={row}
                  householdId={opts.householdId!}
                  disabled={row.type === DocumentType.GeneratedPresentation}
                />
              ),
            },
          currentUser?.isInternalUser && {
            MenuItem: (row) => (
              <CopyInferredDataActionItem
                row={row}
                disabled={row.type === DocumentType.GeneratedPresentation}
                muiMenuItemProps={{
                  divider: canDeleteDocument,
                }}
              />
            ),
          },
          canDeleteDocument && {
            MenuItem: (row) => (
              <DeleteDocumentActionItem
                row={row}
                modalProps={{
                  children:
                    row.associatedEntities.length >= 2 ? (
                      <Typography variant="body-xs">
                        Deleting will remove this document from all shared
                        entities. To remove it for this entity only, unselect it
                        from the shared list.
                      </Typography>
                    ) : undefined,
                }}
                onDelete={() => {
                  opts?.refetch();
                }}
                onDeleteStart={() => markDeleting(row.id)}
                onDeleteError={() => unmarkDeleting(row.id)}
                isDeleting={pendingDeleteIds.has(row.id)}
              />
            ),
          },
        ]),
      }),
    };
  }, [
    canDeleteDocument,
    currentUser?.isInternalUser,
    showDiagnosticLinks,
    opts,
    markDeleting,
    unmarkDeleting,
    pendingDeleteIds,
  ]);

  return useMemo(() => {
    let entityColumn: Column<DocumentsTableRowData> = {
      field: 'associatedEntities',
      headerName: 'Entities or individuals',
      width: 300,
      sortable: false,
      renderCell: TextRenderer({
        text: ({ row }) => {
          const entities = compact([
            ...row.associatedEntities.map(
              (associatedEntity) => associatedEntity.description
            ),
            ...row.associatedTestamentaryEntities.map(
              (associatedTestamentaryEntity) =>
                associatedTestamentaryEntity.description
            ),
          ]);

          if (row.associatedIndividual) {
            entities.push(row.associatedIndividual.displayName);
          }

          return entities.length === 0 ? <EmptyCell /> : entities.join(', ');
        },
        textProps: {
          textOverflow: 'ellipsis',
        },
        textTitle: ({ row }) =>
          compact([
            ...row.associatedEntities.map(
              (associatedEntity) => associatedEntity.description
            ),
          ]).join(', '),
        textAccompaniment: ({ row }) => (
          <AssociatedEntitiesRightContent
            associatedEntities={compact([
              ...row.associatedEntities,
              ...row.associatedTestamentaryEntities,
            ])}
          />
        ),
      }),
      flex: 1,
    };

    const columns: Column<DocumentsTableRowData>[] = compact([
      {
        field: 'name',
        headerName: 'Document',
        width: 300,
        sortable: true,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => row.name,
          lineOneProps: { variant: 'title-xs' },
        }),
        flex: 1,
      },
      aiEnabled && {
        field: 'summaryStatus',
        headerName: 'Document summary',
        width: 200,
        sortable: false,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => {
            if (row.isSummaryProcessing) {
              return 'Generating...';
            }

            // this won't happen very much; really just for presentations
            // and other doc types that don't have AI summaries
            if (!row.hasSummary) {
              return <EmptyCell />;
            }

            return getDocumentAttributionText({
              entryMode: row.entryMode,
              updateUser: row.updateUser,
              updatedAt: row.updatedAt,
              useSummaryCompleteLabel: true,
            });
          },
          textAccompaniment: ({ row }) => {
            if (row.isSummaryProcessing) {
              return (
                <SpinningIcon
                  Icon={RefreshCw05Icon}
                  color={COLORS.TEXT.SECONDARY}
                  size={16}
                />
              );
            }
            return null;
          },
          lineTwo: ({ row }) => {
            if (row.hasSummary && !row.isSummaryProcessing && row.updatedAt) {
              return formatDateToMMDDYY(row.updatedAt);
            }
            return '';
          },
        }),
      },
      entityColumn,
      {
        field: 'type',
        headerName: 'Type',
        width: 160,
        sortable: true,
        valueGetter: (_value: unknown, row: DocumentsTableRowData) =>
          row.displayType,
        renderCell: TextRenderer({
          text: ({ row }) => row.displayType,
        }),
      },
      {
        field: 'createdAt',
        headerName: 'Uploaded',
        width: 160,
        sortable: true,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => formatDateToMMDDYY(row.createdAt),
          lineTwo: ({ row }) => row.createdBy,
        }),
      },
      actions,
    ]);

    const rows: DocumentsTableRowData[] = documents.map((document) => {
      let associatedEntities = [];
      let associatedTestamentaryEntities = [];
      let associatedIndividual = null;
      if (document.clientProfile) {
        associatedIndividual = {
          id: document.clientProfile.id,
          displayName: document.clientProfile.displayName,
        };
      }

      const relationships = getNodes(document.entityDocumentRelationships);

      // Get all entities from relationships (ordered by SOURCE_DOCUMENT first)
      associatedEntities = getEntitiesFromRelationships(relationships).map(
        (entity) => ({
          id: entity.id,
          description: entity.subtype?.displayName ?? '',
          extendedDisplayKind: entity.extendedDisplayKind,
        })
      );

      // Get related testamentary entities
      associatedTestamentaryEntities = getTestamentaryEntitiesFromRelationships(
        relationships
      ).map((entity) => ({
        id: entity.id,
        description: entity.displayName ?? '',
      }));

      // Copy array before sorting since GraphQL results are read-only
      const mostRecentSummary =
        Array.from(document.summaries ?? []).sort((a, b) => {
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        })[0] ?? null;

      return {
        id: document.id,
        name: document.name,
        fileName: document.name,
        associatedEntities,
        associatedTestamentaryEntities,
        associatedIndividual,
        displayType: document.displayType,
        type: document.type,
        createdAt: document.createdAt,
        createdBy: document.createdByUser?.displayName,
        hasSummary: Boolean(mostRecentSummary),
        entryMode: mostRecentSummary?.entryMode ?? null,
        updateUser: mostRecentSummary?.updateUser,
        updatedAt: mostRecentSummary?.updatedAt,
        mostRecentSummaryId: mostRecentSummary?.id ?? null,
        isSummaryProcessing: documentIdsWithProcessingSummaries.has(
          document.id
        ),
        isDefaultOfEntity:
          opts.entityId && document.defaultDocumentOfEntity
            ? document.defaultDocumentOfEntity.id === opts.entityId
            : false,
      };
    });

    return { rows, columns };
  }, [
    aiEnabled,
    actions,
    documents,
    documentIdsWithProcessingSummaries,
    opts.entityId,
  ]);
}
